import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { getApiKeys, deleteApiKey } from "sj-ui-toolkit/src/api/admin/settings";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type KeyType =
  | "platform"
  | "scim"
  | "aspen_read"
  | "aspen_write"
  | "aspen_read_write"
  | "tenant_creator"
  | "mcp"
  | "reporting_api";

interface KeyItem {
  id: string;
  name: string;
  type: KeyType;
  typeLabel: string;
  createdAt: string | null;
  lastUsedAt: string | null;
  raw: unknown;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { label: "All Types", value: "all" as const },
  { label: "SCIM", value: "scim" as KeyType },
  { label: "Aspen Read", value: "aspen_read" as KeyType },
  { label: "Aspen Write", value: "aspen_write" as KeyType },
  { label: "Aspen Read/Write", value: "aspen_read_write" as KeyType },
  { label: "MCP", value: "mcp" as KeyType },
  { label: "Reporting API", value: "reporting_api" as KeyType },
];

// Legacy entitlement names that alias a current configurable entitlement.
const LEGACY_ENTITLEMENT_ALIASES: Record<string, string> = {
  guardian: "aspen_write",
  github_action: "aspen_write",
};

const ENTITLEMENT_TYPES: Record<string, { type: KeyType; typeLabel: string }> = {
  scim: { type: "scim", typeLabel: "SCIM" },
  aspen_read: { type: "aspen_read", typeLabel: "Aspen Read" },
  aspen_write: { type: "aspen_write", typeLabel: "Aspen Write" },
  aspen_read_write: { type: "aspen_read_write", typeLabel: "Aspen Read/Write" },
  tenant_creator: { type: "tenant_creator", typeLabel: "Tenant Creator" },
  reporting_api: { type: "reporting_api", typeLabel: "Reporting API" },
  mcp: { type: "mcp", typeLabel: "MCP Key" },
};

// Order in which to pick a type when a token carries more than one entitlement.
const ENTITLEMENT_PRIORITY = [
  "tenant_creator",
  "scim",
  "mcp",
  "aspen_write",
  "aspen_read",
  "reporting_api",
];

const resolveKeyType = (
  entitlements: string[] | undefined
): { type: KeyType; typeLabel: string } => {
  const canonical = (entitlements ?? [])
    .map(e => e.toLowerCase())
    .map(e => LEGACY_ENTITLEMENT_ALIASES[e] ?? e);

  const fallback = { type: "platform" as KeyType, typeLabel: "Platform API Key" };

  if (canonical.includes("aspen_read") && canonical.includes("aspen_write")) {
    return ENTITLEMENT_TYPES.aspen_read_write ?? fallback;
  }

  const match = ENTITLEMENT_PRIORITY.find(entitlement => canonical.includes(entitlement));
  return match ? (ENTITLEMENT_TYPES[match] ?? fallback) : fallback;
};

// ---------------------------------------------------------------------------
// Column body factories
// ---------------------------------------------------------------------------

const createdAtBody = (rowData: KeyItem) => {
  if (!rowData.createdAt) return "--";
  return (
    <span title={humanDate(rowData.createdAt)}>{humanDateWithoutTime(rowData.createdAt)}</span>
  );
};

const lastUsedAtBody = (rowData: KeyItem) => {
  if (!rowData.lastUsedAt) {
    return rowData.type === "reporting_api" ? "--" : "Never";
  }
  return (
    <span title={humanDate(rowData.lastUsedAt)}>{humanDateWithoutTime(rowData.lastUsedAt)}</span>
  );
};

const migratedLastUsedAtBody = (rowData: KeyItem) => {
  if (!rowData.lastUsedAt) return "Never";
  return (
    <span title={humanDate(rowData.lastUsedAt)}>{humanDateWithoutTime(rowData.lastUsedAt)}</span>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const KeyManagement = () => {
  useBreadcrumb({
    label: "Key Management",
    route: AdminAppRoutes.routes.keyManagement.route,
    display: "none",
    resetTrail: true,
  });

  const { useMigratedApiKeys } = useFlags();

  const toast = useRef<Toast>(null);
  const apiTokensClient = getApiTokensClient();

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<"all" | KeyType>("all");
  const [keys, setKeys] = useState<KeyItem[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROW_COUNT);

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const [tokensRes, reportingRes] = await Promise.all([
        apiTokensClient.listApiTokens(useMigratedApiKeys ? undefined : ["reporting_api"]),
        useMigratedApiKeys ? Promise.resolve({ apiKeys: [] }) : getApiKeys(),
      ]);

      const apiTokenKeys: KeyItem[] = (tokensRes?.data?.apiTokens ?? [])
        .filter(item => item.uuid)
        .map(item => {
          const { type, typeLabel } = resolveKeyType(item.entitlements);
          return {
            id: item.uuid!,
            name: item.label || item.uuid!,
            type,
            typeLabel,
            createdAt: item.createdAt ?? null,
            lastUsedAt: item.resolvedAt ?? null,
            raw: item,
          };
        });

      const reportingKeys: KeyItem[] = (reportingRes?.apiKeys ?? []).map(
        (item: { id: number; name: string }) => ({
          id: String(item.id),
          name: item.name,
          type: "reporting_api",
          typeLabel: "Reporting API Key",
          createdAt: null,
          lastUsedAt: null,
          raw: item,
        })
      );

      setKeys([...apiTokenKeys, ...reportingKeys]);
    } catch (error) {
      console.error("Failed to load keys:", error);
      showLocalToast("error", "Error", "Failed to load keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showLocalToast, apiTokensClient]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const filteredKeys = useMemo(() => {
    const nameFilter = searchTerm.trim().toLowerCase();

    return keys
      .filter(item => {
        const matchesType = selectedType === "all" || item.type === selectedType;
        const matchesName = !nameFilter || item.name.toLowerCase().includes(nameFilter);
        return matchesType && matchesName;
      })
      .sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
  }, [keys, searchTerm, selectedType]);

  useEffect(() => {
    setFirst(0);
  }, [searchTerm, selectedType]);

  const confirmDelete = useCallback(
    (key: KeyItem) => {
      confirmDialog({
        message: `Are you sure you want to delete "${key.name}"? This action cannot be undone.`,
        header: "Delete Key",
        icon: "pi pi-exclamation-triangle",
        defaultFocus: "reject",
        acceptClassName: "p-button-danger",
        accept: async () => {
          try {
            if (key.type === "reporting_api" && !useMigratedApiKeys) {
              await deleteApiKey(Number((key.raw as { id: number }).id));
            } else {
              const { error } = await apiTokensClient.revokeApiToken(
                (key.raw as { uuid: string }).uuid
              );
              if (error) throw error;
            }

            setKeys(prev => prev.filter(item => !(item.type === key.type && item.id === key.id)));
            showLocalToast("info", "Deleted", "Key deleted successfully");
          } catch {
            showLocalToast("error", "Error", "Failed to delete key");
          }
        },
      });
    },
    [showLocalToast, apiTokensClient]
  );

  const actionBody = useCallback(
    (rowData: KeyItem) => (
      <Button
        label="Delete"
        severity="danger"
        text
        size="small"
        disabled={isLoading}
        data-testid={`api-key-delete-${rowData.id}`}
        onClick={() => confirmDelete(rowData)}
      />
    ),
    [isLoading, confirmDelete]
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>Key Management</h1>
      </div>

      <Card>
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
          <h2 className="atlas-h4">All Keys</h2>
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <InputText
              aria-label="Filter keys by name"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Filter by name"
              data-testid="api-keys-search-input"
              className="w-full md:w-72"
            />
            <Dropdown
              aria-label="Filter keys by type"
              value={selectedType}
              options={TYPE_OPTIONS}
              onChange={e => setSelectedType(e.value as "all" | KeyType)}
              pt={{ root: { "data-testid": "api-keys-type-filter" } }}
              className="w-full md:w-64"
            />
          </div>
        </div>

        <DataTable
          value={filteredKeys}
          className="p-datatable-sm"
          tableClassName="dt-responsive-text"
          data-testid="api-keys-table"
          loading={isLoading}
          paginator
          first={first}
          rows={rows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPage={e => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          emptyMessage="No keys match the current filters."
        >
          <Column field="name" header="Name" sortable />
          <Column field="typeLabel" header="Type" sortable />
          <Column field="createdAt" header="Created At" body={createdAtBody} sortable />
          <Column
            field="lastUsedAt"
            header="Last Used"
            body={useMigratedApiKeys ? migratedLastUsedAtBody : lastUsedAtBody}
            sortable
          />
          <Column header="Action" body={actionBody} />
        </DataTable>
      </Card>
    </>
  );
};

export default KeyManagement;
